import React, { useState, useEffect, Fragment } from 'react'
import formatCPF from '@brazilian-utils/format-cpf'
import { format } from 'date-fns'
import { Col, Row, Table, Button, Spinner, Card, Form } from 'react-bootstrap'
import Select from 'react-select'

import { statusPlanoTratamento } from '../../constants/common.constants'
import SystemConstants from '../../constants/system.constants'
import RelatorioService from '../../services/relatorio.service'
import colaboradoresService from '../../services/colaboradores.service'
import perfilService from '../../services/perfil.service'
import { filtrarColaboradoresPorPerfisPermitidos } from '../../utils/colaborador.utils'
import PageLoading from '../Common/PageLoading'
import Paginator from '../Common/Paginator'
import CSVDownload from './CSVDownload'

const debitoOptions = [
  // filtro.campo = 'debito'
  { label: 'Todos', value: null },
  { label: 'Com Débito', value: true },
  { label: 'Sem Débito', value: false },
]

export default function PacientesSemAgendamento({ selectedRelatorio }) {
  const itensPorPagina = 20
  const user = JSON.parse(localStorage.getItem(SystemConstants.getUser()) || '{}')

  const getResponsavelPadrao = (options = []) => {
    if (!user?.colaboradorId) return null
    const encontrado = options.find((c) => c.value === user.colaboradorId)
    return encontrado || { label: user.name || 'Colaborador logado', value: user.colaboradorId }
  }

  const [isLoading, setLoading] = useState(false)
  const [itensLista, setItensLista] = useState([])
  const [cabecalhoRelatorio, setCabecalhoRelatorio] = useState([])
  const [resumo, setResumo] = useState(null)
  const [pagina, setPagina] = useState(0)
  const [totalPaginas, setTotalPaginas] = useState(0)
  const [itensCSV, setItensCSV] = useState([])
  const [isLoadingCSV, setLoadingCSV] = useState(false)
  const [downloadCSV, setDownloadCSV] = useState(false)
  const [especialidadeOptions, setEspecialidadeOptions] = useState([])
  const [selectedEspecialidade, setSelectedEspecialidade] = useState(null)
  const [selectedDebito, setSelectedDebito] = useState(debitoOptions[0])
  const [colaboradoresOptions, setColaboradoresOptions] = useState([])
  const [selectedResponsavel, setSelectedResponsavel] = useState(() => getResponsavelPadrao())
  const [filterLoading, setFilterLoading] = useState(true)
  const [filtrosProntos, setFiltrosProntos] = useState(false)

  useEffect(() => {
    didMount()
    setCabecalhoRelatorio([
      'Nome',
      'Dependente',
      'CPF',
      'Telefone Principal',
      'Whats App',
      'E-mail',
      'Nº Plano',
      'Responsável',
      'Dentista',
      'Status',
      'Possui Débito',
    ])
  }, [])

  useEffect(() => {
    if (!filtrosProntos || !selectedEspecialidade) return
    handleSearch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagina, selectedEspecialidade, selectedDebito, selectedResponsavel, filtrosProntos])

  useEffect(() => {
    if (downloadCSV) {
      setTimeout(() => {
        setDownloadCSV(false)
      }, 500)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itensCSV, downloadCSV])

  const didMount = async () => {
    setFilterLoading(true)
    try {
      const [{ data: especialidades }, { data: colaboradores }, { data: perfis }] = await Promise.all([
        colaboradoresService.getAllEspecialidades(),
        colaboradoresService.getAllColaboradores(),
        perfilService.getPerfis(),
      ])

      const optionsEspecialidade = (especialidades || []).map(({ nome, id }) => ({
        label: nome,
        value: id,
      }))
      setEspecialidadeOptions(optionsEspecialidade)
      setSelectedEspecialidade(optionsEspecialidade[0] || null)

      const optionsColaboradores = filtrarColaboradoresPorPerfisPermitidos(colaboradores, perfis).map(
        ({ nome, id }) => ({ label: nome, value: id })
      )
      setColaboradoresOptions(optionsColaboradores)
      setSelectedResponsavel(getResponsavelPadrao(optionsColaboradores))
      setFiltrosProntos(true)
    } catch (error) {
      console.error(error)
      setFiltrosProntos(true)
    }
    setFilterLoading(false)
  }

  const montarFiltros = () => {
    const filtros = [
      { campo: 'especialidadeId', valor: selectedEspecialidade.value },
      { campo: 'debito', valor: selectedDebito.value },
    ]
    if (selectedResponsavel?.value) {
      filtros.push({ campo: 'responsavelId', valor: selectedResponsavel.value })
    }
    return filtros
  }

  const getValuesLista = async () => {
    const filtros = montarFiltros()
    setLoadingCSV(true)
    const { lista } = await RelatorioService.getRegistrosPacientesSemAgendamento(filtros, 1, 999999)
    const valores = []
    lista.dados.forEach((item) => {
      const statusPlano = statusPlanoTratamento.find((s) => s.value === item.statusTratamento)
      const itemRelatorio = new Object()
      itemRelatorio.nome = item.nome
      itemRelatorio.nomeDependente = item.nomeDependente ? item.nomeDependente : '-'
      itemRelatorio.cpf = formatCPF(item.cpf)
      itemRelatorio.telefonePrincipal = item.telefonePrincipal
      itemRelatorio.telefonesWhatsApp = item.telefonesWhatsApp || '-'
      itemRelatorio.emails = item.emails || '-'
      itemRelatorio.numeroPlano = item.numeroPlano
      itemRelatorio.responsavel = item.responsavel || '-'
      itemRelatorio.dentista = item.dentista
      statusPlano ? (itemRelatorio.statusTratamento = statusPlano.label) : (itemRelatorio.statusTratamento = '-')
      itemRelatorio.temDebito = item.temDebito ? 'Sim' : 'Não'
      valores.push(Object.values(itemRelatorio))
    })
    setItensCSV(valores)
    setDownloadCSV(true)
    setLoadingCSV(false)
    return {
      data: valores,
      headers: cabecalhoRelatorio,
      filename: `${selectedRelatorio.label} - ${format(new Date(), 'dd-MM-yyyy')}.csv`,
    }
  }

  const handleSearch = async () => {
    setLoading(true)
    try {
      const filtros = montarFiltros()
      const { resumo, lista } = await RelatorioService.getRegistrosPacientesSemAgendamento(
        filtros,
        pagina + 1,
        itensPorPagina
      )
      setResumo(resumo)
      setItensLista(lista.dados)
      setTotalPaginas(lista.totalPaginas)
    } catch (error) {
      console.error(error)
    }
    setLoading(false)
  }

  const handleClear = () => {
    setSelectedEspecialidade(especialidadeOptions[0])
    setSelectedDebito(debitoOptions[0])
    setSelectedResponsavel(getResponsavelPadrao(colaboradoresOptions))
    setPagina(0)
  }

  const handleChangeEspecialidade = (option) => {
    setSelectedEspecialidade(option)
    setPagina(0)
  }

  const handleChangeDebito = (option) => {
    setSelectedDebito(option)
    setPagina(0)
  }

  const handleChangeResponsavel = (option) => {
    setSelectedResponsavel(option)
    setPagina(0)
  }

  const handlePagina = (data) => setPagina(data.selected)

  const renderItens = () =>
    itensLista?.length > 0 ? (
      <Fragment>
        <div className='mt-4 d-flex justify-content-end align-items-center'>
          <CSVDownload asyncExportMethod={getValuesLista}>
            <Button variant='outline-secondary' className='mb-3'>
              {isLoadingCSV ? <Spinner animation='border' size='sm' variant='light' /> : 'Gerar CSV'}
            </Button>
          </CSVDownload>
        </div>

        <Card className='mb-4 py-2'>
          <Row>
            {resumo?.map(({ descricao, valor }, index) => (
              <Col key={index} className='d-flex align-items-center justify-content-center flex-column'>
                <small className='text-center text-muted m-0'>{!!descricao ? descricao.toUpperCase() : '-'}</small>
                <h4 className='text-center m-0 font-weight-bold'>{valor || '-'}</h4>
              </Col>
            ))}
          </Row>
        </Card>

        <Table hover size='sm' className='border rounded-0 shadow-sm' style={{ backgroundColor: 'white' }}>
          <thead>
            <tr className='text-center'>
              <th>Nome</th>
              <th>Dependente</th>
              <th>CPF</th>
              <th>Telefone Principal</th>
              <th>WhatsApp</th>
              <th>E-mail</th>
              <th>Nº Plano</th>
              <th>Responsável</th>
              <th>Dentista</th>
              <th>Status</th>
              <th>Possui Débito</th>
            </tr>
          </thead>
          <tbody>
            {itensLista.map((item, i) => {
              const {
                nome,
                nomeDependente,
                cpf,
                telefonePrincipal,
                telefonesWhatsApp,
                emails,
                numeroPlano,
                responsavel,
                dentista,
                statusTratamento,
                procedimentoProtetico,
                temDebito,
              } = item
              const statusPlano = statusPlanoTratamento.find(({ value }) => value === statusTratamento)
              return (
                <tr
                  key={i}
                  className='text-center'
                  style={{ fontSize: 'smaller', backgroundColor: procedimentoProtetico ? 'rgb(208, 235, 251)' : null }}
                >
                  <td>{nome.toUpperCase()}</td>
                  <td>{nomeDependente ? nomeDependente.toUpperCase() : '-'}</td>
                  <td>{formatCPF(cpf)}</td>
                  <td>{telefonePrincipal}</td>
                  <td>{telefonesWhatsApp || '-'}</td>
                  <td>{emails || '-'}</td>
                  <td>{numeroPlano || '-'}</td>
                  <td>{responsavel ? responsavel.toUpperCase() : '-'}</td>
                  <td>{dentista || '-'}</td>
                  <td>{statusPlano ? statusPlano.label : '-'}</td>
                  <td>{temDebito ? 'Sim' : 'Não'}</td>
                </tr>
              )
            })}
          </tbody>
        </Table>
        {totalPaginas > 1 && <Paginator handlePagina={handlePagina} pageCount={totalPaginas} paginaAtual={pagina} />}
      </Fragment>
    ) : (
      <div className='mt-4'>Nenhum registro encontrado</div>
    )

  const renderLoading = () => <PageLoading />

  return (
    <Fragment>
      <Row>
        <Col md={3} className='mb-2'>
          <Form.Group className='mb-0'>
            <Form.Label>Especialidade</Form.Label>
            <Select
              placeholder='Selecione'
              onChange={handleChangeEspecialidade}
              value={selectedEspecialidade}
              options={especialidadeOptions}
            />
            {filterLoading && <Spinner animation='border' variant='primary' size='sm' style={spinnerStyle} />}
          </Form.Group>
        </Col>
        <Col md={3} className='mb-2'>
          <Form.Group className='mb-0'>
            <Form.Label>Responsável</Form.Label>
            <Select
              placeholder='Todos'
              onChange={handleChangeResponsavel}
              value={selectedResponsavel}
              options={colaboradoresOptions}
              isClearable
              noOptionsMessage={() => 'Nenhum colaborador'}
            />
          </Form.Group>
        </Col>
        <Col md={3} className='mb-2'>
          <Form.Group className='mb-0'>
            <Form.Label>Débito</Form.Label>
            <Select placeholder='Selecione' onChange={handleChangeDebito} value={selectedDebito} options={debitoOptions} />
          </Form.Group>
        </Col>
        <Col md={3} className='mb-2 d-flex justify-content-end align-items-end'>
          <Button disabled={filterLoading} onClick={handleClear} variant='outline-danger'>
            Limpar
          </Button>
        </Col>
      </Row>
      {isLoading || filterLoading ? renderLoading() : renderItens()}
    </Fragment>
  )
}

const spinnerStyle = {
  position: 'absolute',
  top: '10px',
  right: '50px',
}
